import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { useCurrencyRates } from '@/api/currency/queries/useCurrencyRates'
import { useUploadUserPkgReceipt } from '@/api/user-package/mutations/useUploadUserPkgReceipt'
import { useUserPkgs } from '@/api/user-package/queries/useUserPkgs'
import { UploadUserPkgReceiptPayload } from '@/api/user-package/types/UploadUserPkgReceiptPayload'
import { PkgWarehouse, UserPkg } from '@/api/user-package/types/UserPkg'
import { AppBreadcrumbs } from '@/components/AppBreadcrumbs'
import { AppTooltip } from '@/components/AppTooltip'
import { ConfirmDialog } from '@/components/ConfirmDialog'
import AedIcon from '@/components/icons/aed.svg?react'
import AttentionIcon from '@/components/icons/attention.svg?react'
import EurIcon from '@/components/icons/eur.svg?react'
import GbpIcon from '@/components/icons/gbp.svg?react'
import InfoIcon from '@/components/icons/info.svg?react'
import UsdIcon from '@/components/icons/usd.svg?react'
import { TextField } from '@/components/TextField'
import { Button } from '@/components/ui/button'
import { queryClient } from '@/lib/queryClient'
import { QUERY_KEYS } from '@/lib/queryKeys'
import { RoutePath } from '@/RoutePath'

import { PkgPreview } from '../ClientPkg/components/PkgPreview'
import { Stepper } from './components/Stepper'
import { UploadArea } from './components/UploadArea'

const warehouseToIcon = {
  [PkgWarehouse.GR]: <EurIcon />,
  [PkgWarehouse.USA]: <UsdIcon />,
  [PkgWarehouse.UK]: <GbpIcon />,
  [PkgWarehouse.UAE]: <AedIcon />,
}

export default function ReceiptUploadPage() {
  const navigate = useNavigate()
  const { mutateAsync: uploadUserPkgReceipt, isPending: isUploadingReceipt } = useUploadUserPkgReceipt()
  const { data: userPkgs } = useUserPkgs()
  const { data: currencyRates } = useCurrencyRates()
  const EURToUSD = currencyRates?.EURToUSD ?? 0
  const GBPToUSD = currencyRates?.GBPToUSD ?? 0
  const AEDToUSD = currencyRates?.AEDToUSD ?? 0
  const { pkgId } = useParams<{ pkgId: string }>()
  const pkg = userPkgs?.find(p => p.id === pkgId)

  // UI state
  const [showErrorModal, setShowErrorModal] = useState(false)
  const [errorModalText, setErrorModalText] = useState('')
  const [showCustomsDeclarationModal, setShowCustomsDeclarationModal] = useState(false)
  const [verifyInfoModal, setVerifyInfoModal] = useState(false)

  // Receipt data
  const [receiptFile, setReceiptFile] = useState<File | null>(null)
  const [pkgValue, setPkgValue] = useState(pkg?.price?.toString() || '')

  const convertSumToUSD = () => {
    if (!pkgValue || parseFloat(pkgValue) === 0) return 0
    const value = parseFloat(pkgValue)
    switch (pkg?.warehouse) {
      case 'GR': // Euro to USD
        return value * EURToUSD
      case 'USA': // Already in USD
        return value
      case 'UK': // GBP to USD
        return value * GBPToUSD
      case 'UAE': // AED to USD
        return value * AEDToUSD
      default:
        return value
    }
  }

  const getSumWarning = () => {
    if (!pkg) return ''
    const value = parseFloat(pkgValue)
    switch (pkg.warehouse) {
      case 'GR':
        if (value >= 1100) return 'שווי החבילה גבוה. שים לב שלא טעית.'
        else if (value <= 40 && value !== 0) return 'שווי החבילה נמוך. שים לב שלא טעית.'
        break
      case 'USA':
        if (value >= 300) return 'שווי החבילה גבוה. שים לב שלא טעית.'
        else if (value <= 30 && value !== 0) return 'שווי החבילה נמוך. שים לב שלא טעית.'
        break
      case 'UK':
        if (value >= 230) return 'שווי החבילה גבוה. שים לב שלא טעית.'
        else if (value <= 22 && value !== 0) return 'שווי החבילה נמוך. שים לב שלא טעית.'
        break
      case 'UAE':
        if (value >= 1100) return 'שווי החבילה גבוה. שים לב שלא טעית.'
        else if (value <= 100 && value !== 0) return 'שווי החבילה נמוך. שים לב שלא טעית.'
        break
    }
    return ''
  }

  const proceedWithUpload = async () => {
    if (!pkg) return

    try {
      const updateParams = {} as UploadUserPkgReceiptPayload
      if (receiptFile) {
        updateParams.file = receiptFile
      }
      if (pkg.price !== parseFloat(pkgValue)) {
        updateParams.price = parseFloat(pkgValue)
      }
      if (Object.keys(updateParams).length > 0) {
        updateParams.packageId = pkg.id
        await uploadUserPkgReceipt(updateParams)
      }

      // Redirect to payment page or my packages
      if (pkg.isPaid || pkg.isPhotoRequestActive || pkg.isReturnRequestActive || pkg.isShrinkRequestActive) {
        navigate(RoutePath.ClientPkg)
      } else {
        navigate(`${RoutePath.PaymentShipping}/${pkgId}`)
      }
    } catch (error) {
      console.log('Error uploading file: ', error)
      setErrorModalText('הייתה שגיאה בהעלאת הקובץ. אנא נסה שנית או פנה לשירות הלקוחות.')
      setShowErrorModal(true)
    }
  }

  const handleSubmit = () => {
    // Check if price exists
    if (!pkgValue || parseFloat(pkgValue) < 1) {
      setErrorModalText('יש להכניס שווי חבילה.')
      setShowErrorModal(true)
      return
    }

    // Check for allowed files formats
    if (!receiptFile && !pkg!.buyingReciptKey) {
      setErrorModalText('נא להעלות חשבונית.')
      setShowErrorModal(true)
      return
    }

    // Check if package value in USD is above $1000 and show customs declaration popup
    const sumInUSD = convertSumToUSD()
    if (sumInUSD > 1000) {
      setShowCustomsDeclarationModal(true)
      return
    }
    setVerifyInfoModal(true)
  }

  const getLocalCurrency = () => {
    if (!pkg) return ''
    switch (pkg.warehouse) {
      case 'GR':
        return 'אירו'
      case 'USA':
        return 'דולר'
      case 'UK':
        return 'פאונד'
      case 'UAE':
        return 'דירהם'
      default:
        return ''
    }
  }
  const onSetReceiptFile = (file: File | null) => {
    if (!file) {
      queryClient.setQueryData<UserPkg[]>([QUERY_KEYS.USER_PACKAGES], old =>
        old?.map(item => (item.id === pkg!.id ? { ...item, buyingReciptKey: '' } : item))
      )
    }
    setReceiptFile(file)
  }
  if (!pkg) {
    return null
  }

  return (
    <div className='mx-auto h-full max-w-5xl p-6' dir='rtl'>
      <div className='mb-6'>
        <AppBreadcrumbs
          steps={[
            { label: 'החבילות שלי', to: RoutePath.ClientPkg },
            { label: 'הצהרת חבילה', to: RoutePath.ReceiptUpload },
          ]}
        />
      </div>
      <div className='mx-auto max-w-xl pb-12'>
        <div className='mb-6 flex justify-center'>
          <Stepper currentStep={0} totalSteps={3} />
        </div>

        {/* Main Content */}
        <div className='flex flex-col gap-6'>
          {/* Title Section */}
          <div className=''>
            <h1 className='text-text-primary mb-2 text-2xl font-medium'>הצהרת חבילה</h1>
            <p className='text-text-secondary text-sm leading-relaxed'>
              כדי שנוכל לשלוח את החבילה אליך – צריך רק למלא כמה פרטים ולצרף קבלה תקינה.
            </p>
          </div>

          {/* Package Card */}
          <PkgPreview pkg={pkg!} currentView='all' hideActions hideStatus />

          {/* Upload Section */}
          <div className='space-y-4'>
            <h3 className='text-text-primary text-sm font-medium'>העלאת קבלה</h3>
            <UploadArea onChangeFile={onSetReceiptFile} previewUrl={pkg.buyingReciptKey} />
            <a href='https://www.instagram.com/p/Cao7zAmscWE/?igshid=YmMyMTA2M2Y=' target='_blank' rel='noreferrer'>
              <Button variant='link' className='gap-1'>
                <InfoIcon />
                מהי הקבלה שלי?
              </Button>
            </a>
          </div>

          {/* Price Input Section */}
          <div className='space-y-4'>
            <AppTooltip content='הסכום הכולל של שווי הפריטים בלבד! ללא מיסים מקומיים ועלויות שילוח מקומיות. את הסכום נדרש לכתוב במטבע המקומי של המחסן אליו הזמנת (דולר, אירו וכד׳)'>
              <div className='flex items-center gap-2'>
                <h3 className='text-text-primary text-sm font-medium'>מה שווי החבילה?</h3>
                <InfoIcon />
              </div>
            </AppTooltip>
            <TextField
              trailingIcon={warehouseToIcon[pkg.warehouse]}
              value={pkgValue}
              type='number'
              inputMode='numeric'
              placeholder={getLocalCurrency()}
              onChange={e => setPkgValue(e.target.value)}
              supportingText={getSumWarning() || `שים לב! המטבע מתעדכן אוטומטית בבחירת החבילה`}
              error={getSumWarning() !== ''}
            />
          </div>

          {/* Terms and Submit */}
          <div className='flex flex-col items-center gap-2 pt-4'>
            <Button
              onClick={handleSubmit}
              disabled={isUploadingReceipt}
              className='w-full max-w-[335px]'
              size='lg'
              loading={isUploadingReceipt}
            >
              המשך לבחירת סוג משלוח
            </Button>

            <p className='text-text-secondary text-center text-xs'>ודא כי כל הנתונים נכונים וכי ההצהרה והשווי תואמים להזמנה שלך</p>
          </div>
        </div>

        <ConfirmDialog
          isOpen={showErrorModal}
          onClose={() => setShowErrorModal(false)}
          onConfirm={() => setShowErrorModal(false)}
          confirmText='חזרה'
          Icon={AttentionIcon}
          title='שים לב!'
          content={errorModalText}
        />
        <ConfirmDialog
          isOpen={showCustomsDeclarationModal}
          onClose={() => setShowCustomsDeclarationModal(false)}
          onConfirm={() => {
            setShowCustomsDeclarationModal(false)
            proceedWithUpload()
          }}
          confirmText='אישור'
          cancelText='חזרה'
          Icon={AttentionIcon}
          title='הודעה חשובה'
          content='בהתאם לחוק, חבילות בשווי מעל 1000$ נדרשות במילוי טופס ייפוי כח לעמיל המכס לטובת שחרורם.'
        />
        <ConfirmDialog
          isOpen={verifyInfoModal}
          onClose={() => setVerifyInfoModal(false)}
          onConfirm={() => {
            setVerifyInfoModal(false)
            proceedWithUpload()
          }}
          confirmText='יש אישור'
          cancelText='חזרה'
          Icon={AttentionIcon}
          content='אנא בדוק כי ההצהרה שהעלית ושווי החבילה שלך נכונים ומדויקים'
          title='רגע אחד לפני שממשיכים...'
        />
      </div>
    </div>
  )
}
